// Picker de "sobre qué trabajamos" — tool `pick_creacion` del asistente v2.
//
// Se muestra cuando el usuario pide RETOCAR/CAMBIAR algo que ya existe ("hacela
// más clara", "cambiale el fondo a la remera") y el agente no sabe con certeza
// a qué pieza se refiere (ADR-2026-09-08-pick-creacion).
//
// La card SUGIERE antes de preguntar: trae sola las últimas imágenes generadas
// del usuario y, si hay una sola, la muestra grande para confirmar de un toque
// ("¿Es esta?"). Con varias, se elige tocando. Y siempre deja las dos puertas
// grandes por si lo que busca no está entre las recientes: el historial
// completo de generadas y el catálogo de productos.
//
// Devuelve `{ tipo: 'creacion', image_id }` o `{ tipo: 'producto', product_id }`
// — lo que el agente después pasa en `reference_images` de generate_image.

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, ImageIcon, Package } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { marcaParaFiltrar, orDeMarca } from '../../../utils/filtroDeMarca';
import CachedImg from '../../../components/CachedImg';
import { useAgentEnv } from '../agentEnvContext';
import { useUserProducts } from '../productsContext';
import GeneratedImagesPicker from '../GeneratedImagesPicker';
import { ProductFullMenu, signRecentThumb } from '../ChatPanelV2';
import type { V2ToolCard } from '../useV2Chat';

/** Qué puede elegirse. Lo decide el agente según lo que pidió el usuario. */
export type FuentePickCreacion = 'creacion' | 'producto' | 'ambas';

export interface RespuestaPickCreacion {
  tipo: 'creacion' | 'producto';
  image_id?: string;
  product_id?: string;
  /** Sólo para el resumen de la card resuelta — el agente no lo necesita. */
  thumbUrl?: string;
  etiqueta?: string;
}

interface Reciente {
  id: string;
  thumbUrl: string;
  label: string;
}

export const PickCreacionCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  onResolve: (r: RespuestaPickCreacion) => void;
}> = ({ card, active, onResolve }) => {
  const input = (card.input ?? {}) as {
    message?: string;
    fuente?: FuentePickCreacion;
    limit?: number;
  };
  const fuente: FuentePickCreacion = input.fuente ?? 'ambas';
  const muestraCreaciones = fuente !== 'producto';
  const muestraProductos = fuente !== 'creacion';
  const limit = Math.min(9, Math.max(1, Number(input.limit) || 4));

  const { companyId, brandId, variasMarcas } = useAgentEnv();
  // Las creaciones ofrecidas son las de la MARCA ACTIVA, igual que las de la home y del
  // historial (ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat): ofrecer para retocar
  // una pieza de otra marca es lo mismo que mezclarlas.
  const marcaDelFiltro = marcaParaFiltrar(brandId, variasMarcas === true);
  const productos = useUserProducts();
  const [loading, setLoading] = useState(muestraCreaciones);
  const [recientes, setRecientes] = useState<Reciente[]>([]);
  const [elegida, setElegida] = useState<string | null>(null);
  const [picker, setPicker] = useState<'none' | 'generadas' | 'productos'>('none');

  // Últimas generaciones del usuario. SÓLO imágenes: la card existe para
  // retocar/derivar, y un video no se puede pasar como referencia.
  // Depende de `companyId` porque al volver de otra pantalla arranca en null y
  // carga async — sin la dep, la grilla quedaba vacía para siempre (mismo
  // problema que ya tuvo RecentMediaCard).
  useEffect(() => {
    if (!muestraCreaciones) return;
    // Ya respondida: el resumen sale de `card.response`, no hace falta ir a la
    // base. Sin esto, cada card vieja del stream refetchea al remontar el chat.
    if (card.status !== 'pending') return;
    if (!companyId) return;
    let vivo = true;
    setLoading(true);
    (async () => {
      const out: Reciente[] = [];
      try {
        let q = supabase
          .from('imagenes_generadas')
          .select('id, tipo, file_name, prompt, variantes:variantes(nombre)')
          .eq('id_company', companyId)
          .eq('active', true)
          .not('tipo', 'in', '("video","video_pro")');
        if (marcaDelFiltro) q = q.or(orDeMarca(marcaDelFiltro));
        const { data } = await q
          .order('created_at', { ascending: false })
          .limit(limit);
        for (const r of ((data as any[]) || [])) {
          const thumbUrl = await signRecentThumb(
            companyId,
            r.file_name || '',
            false,
          );
          const raw = String(r.prompt || r.variantes?.nombre || '').trim();
          out.push({
            id: String(r.id),
            thumbUrl,
            label: raw ? raw.slice(0, 90) : 'imagen',
          });
        }
      } catch {
        /* sin datos → cae en el estado vacío, que igual ofrece las puertas */
      }
      if (!vivo) return;
      setRecientes(out);
      // Una sola candidata: queda elegida de entrada y el botón dice "Sí, esa".
      // Es la diferencia entre preguntar y sugerir.
      if (out.length === 1) setElegida(out[0].id);
      setLoading(false);
    })();
    return () => {
      vivo = false;
    };
  }, [companyId, marcaDelFiltro, limit, muestraCreaciones, card.status]);

  const productoDe = (id: string) => productos.find((p) => String(p.id) === id);

  const resolverCreacion = (id: string) => {
    const it = recientes.find((r) => r.id === id);
    onResolve({
      tipo: 'creacion',
      image_id: id,
      thumbUrl: it?.thumbUrl,
      etiqueta: it?.label,
    });
  };

  const resolverProducto = (id: string) => {
    const p = productoDe(id);
    onResolve({
      tipo: 'producto',
      product_id: id,
      thumbUrl: p?.imageUrl,
      etiqueta: p?.name,
    });
  };

  // ── Resumen (card ya respondida) ──────────────────────────────────
  if (card.status === 'resolved' || card.status === 'cancelled') {
    const r: RespuestaPickCreacion | undefined = card.response;
    return (
      <div className="p-4">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
          {r?.tipo === 'producto' ? 'Producto' : 'Creación'}
        </p>
        {r ? (
          <div className="flex items-center gap-2.5">
            {r.thumbUrl && (
              <CachedImg
                src={r.thumbUrl}
                alt=""
                className="w-12 h-12 rounded-xl object-cover bg-neutral-100 shrink-0"
              />
            )}
            <span className="text-[13.5px] text-brand-black/85 line-clamp-2">
              {r.etiqueta || 'Elegida'}
            </span>
          </div>
        ) : (
          <p className="text-[13px] text-[#3C3C43]/55">Sin elegir</p>
        )}
      </div>
    );
  }

  const unaSola = recientes.length === 1;

  // Las dos puertas grandes. Van SIEMPRE (también con candidatas): lo que el
  // usuario busca puede no estar entre las últimas, y sin esto la única salida
  // sería escribir "no, otra" y esperar otro turno.
  const puertas = (
    <div className="flex flex-col gap-2">
      {muestraCreaciones && (
        <button
          type="button"
          disabled={!active}
          onClick={() => setPicker('generadas')}
          className="w-full inline-flex items-center gap-2.5 px-3.5 py-3 rounded-2xl bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50 text-brand-black/85 text-[14px] font-semibold transition-colors"
        >
          <ImageIcon className="w-4 h-4 shrink-0 text-brand-primary" />
          Ver todas mis imágenes
          <ChevronRight className="w-4 h-4 ml-auto text-[#3C3C43]/40" />
        </button>
      )}
      {muestraProductos && (
        <button
          type="button"
          disabled={!active}
          onClick={() => setPicker('productos')}
          className="w-full inline-flex items-center gap-2.5 px-3.5 py-3 rounded-2xl bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50 text-brand-black/85 text-[14px] font-semibold transition-colors"
        >
          <Package className="w-4 h-4 shrink-0 text-brand-primary" />
          Elegir de mis productos
          <ChevronRight className="w-4 h-4 ml-auto text-[#3C3C43]/40" />
        </button>
      )}
    </div>
  );

  return (
    <div className="p-5">
      <p className="text-[15px] font-semibold text-brand-black/90 mb-3">
        {input.message || '¿Sobre cuál querés trabajar?'}
      </p>

      {muestraCreaciones && loading && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="aspect-[4/5] rounded-xl bg-neutral-100 animate-pulse"
            />
          ))}
        </div>
      )}

      {muestraCreaciones && !loading && recientes.length > 0 && (
        <>
          {/* Una sola: grande y centrada, para confirmarla de un toque.
              Varias: grilla para elegir. */}
          <div
            className={
              unaSola
                ? 'mb-3 flex justify-center'
                : 'mb-3 grid grid-cols-3 gap-2'
            }
          >
            {recientes.map((it) => {
              const sel = elegida === it.id;
              return (
                <button
                  key={it.id}
                  type="button"
                  disabled={!active}
                  onClick={() => setElegida(sel && !unaSola ? null : it.id)}
                  title={it.label}
                  aria-pressed={sel}
                  className={`relative rounded-xl overflow-hidden bg-neutral-100 transition-all disabled:opacity-60 ${
                    unaSola ? 'w-[164px] aspect-[4/5]' : 'aspect-[4/5]'
                  } ${
                    sel
                      ? 'ring-2 ring-brand-primary ring-offset-2'
                      : 'ring-1 ring-black/[0.06] hover:ring-brand-primary/40'
                  }`}
                >
                  {it.thumbUrl ? (
                    <CachedImg
                      src={it.thumbUrl}
                      alt={it.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full block bg-neutral-100" />
                  )}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            disabled={!active || !elegida}
            onClick={() => elegida && resolverCreacion(elegida)}
            className="w-full inline-flex items-center justify-center py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors mb-3"
          >
            {unaSola ? 'Sí, esa' : 'Usar esta'}
          </button>
        </>
      )}

      {muestraCreaciones && !loading && recientes.length === 0 && (
        <p className="text-[14px] text-brand-black/85 leading-snug mb-3">
          No encontré imágenes generadas recientes.
        </p>
      )}

      {puertas}

      {/* Pickers reales — los MISMOS que usa el menú "+ Agregar" del composer,
          así lo que ve acá es idéntico a lo que vería adjuntando a mano. */}
      <GeneratedImagesPicker
        open={picker === 'generadas'}
        companyId={companyId}
        onCancel={() => setPicker('none')}
        onAccept={(picks) => {
          setPicker('none');
          const p = picks[0];
          if (!p) return;
          onResolve({
            tipo: 'creacion',
            image_id: String(p.id),
            thumbUrl: p.previewUrl,
            etiqueta: 'Imagen del historial',
          });
        }}
      />
      {picker === 'productos' && (
        <ProductFullMenu
          initialSelected={[]}
          allowMulti={false}
          onCancel={() => setPicker('none')}
          onAccept={(ids) => {
            setPicker('none');
            if (ids[0]) resolverProducto(ids[0]);
          }}
        />
      )}
    </div>
  );
};
